/**
 * GeocodeSearchBar — buscador de direcciones para el flujo de georreferenciar
 * plano contra el mapa OSM.
 *
 * Tipea "Hites Alameda" → autocomplete con resultados Nominatim → click en
 * uno dispara onSelect({ lat, lon, display_name }). El padre centra el mapa
 * MapLibre en esas coords y el operador empieza a arrastrar las esquinas
 * del plano.
 *
 * Hace debounce de 350ms antes de pegarle al backend para no abusar de
 * Nominatim (rate limit 1 req/s en OSM público).
 *
 * Props:
 *   token       — JWT del usuario (para apiFetch)
 *   onSelect    — (result) => void cuando el operador elige uno
 *   placeholder — texto del input (default "Buscar dirección o lugar...")
 */

import { useState, useEffect, useRef } from 'react'


export default function GeocodeSearchBar({
  token,
  onSelect,
  placeholder = 'Buscar dirección o lugar...',
}) {
  const [q, setQ] = useState('')
  const [results, setResults] = useState([])
  const [loading, setLoading] = useState(false)
  const [open, setOpen] = useState(false)
  const debounceRef = useRef(null)
  const inputRef = useRef(null)

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    if (!q || q.length < 3) {
      setResults([])
      return
    }
    debounceRef.current = setTimeout(async () => {
      setLoading(true)
      try {
        const r = await fetch(
          `/api/site-floor-plans/geocode?q=${encodeURIComponent(q)}`,
          { headers: { Authorization: `Bearer ${token}` } },
        )
        if (!r.ok) throw new Error(`HTTP ${r.status}`)
        const data = await r.json()
        setResults(Array.isArray(data) ? data : [])
        setOpen(true)
      } catch (e) {
        // Silenciar — el operador ve "sin resultados"
        setResults([])
      } finally {
        setLoading(false)
      }
    }, 350)
    return () => clearTimeout(debounceRef.current)
  }, [q, token])

  const handleSelect = (result) => {
    setQ(result.display_name)
    setOpen(false)
    if (onSelect) onSelect(result)
  }

  return (
    <div style={{ position: 'relative', width: '100%', maxWidth: 480 }}>
      <div style={{ position: 'relative' }}>
        <input
          ref={inputRef}
          type="text"
          value={q}
          onChange={e => setQ(e.target.value)}
          onFocus={() => results.length > 0 && setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          placeholder={placeholder}
          style={{
            width: '100%',
            padding: '10px 14px 10px 38px',
            fontSize: 13,
            border: '1px solid var(--border, #d1d5db)',
            borderRadius: 6,
            background: 'var(--bg-surface, white)',
            color: 'var(--text-primary, #111)',
            boxSizing: 'border-box',
          }}
        />
        {/* Ícono lupa (SVG inline para no depender de lucide en este punto) */}
        <svg
          width="16" height="16" viewBox="0 0 24 24"
          fill="none" stroke="currentColor" strokeWidth="2"
          style={{
            position: 'absolute',
            left: 12, top: '50%', transform: 'translateY(-50%)',
            color: 'var(--text-muted, #6b7280)',
            pointerEvents: 'none',
          }}
        >
          <circle cx="11" cy="11" r="8" />
          <line x1="21" y1="21" x2="16.65" y2="16.65" />
        </svg>
        {loading && (
          <span style={{
            position: 'absolute', right: 14, top: '50%',
            transform: 'translateY(-50%)',
            fontSize: 11, color: 'var(--text-muted, #6b7280)',
          }}>
            buscando…
          </span>
        )}
      </div>

      {open && results.length > 0 && (
        <div style={{
          position: 'absolute',
          top: '100%', left: 0, right: 0,
          marginTop: 4,
          background: 'var(--bg-surface, white)',
          border: '1px solid var(--border, #d1d5db)',
          borderRadius: 6,
          maxHeight: 320,
          overflowY: 'auto',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          zIndex: 20,
        }}>
          {results.map((r, i) => (
            <button
              key={`${r.lat},${r.lon},${i}`}
              onMouseDown={() => handleSelect(r)}
              style={{
                display: 'block', width: '100%',
                textAlign: 'left',
                padding: '10px 14px',
                border: 'none',
                borderBottom: i < results.length - 1 ? '1px solid var(--border, #e5e7eb)' : 'none',
                background: 'transparent',
                color: 'var(--text-primary, #111)',
                fontSize: 12,
                cursor: 'pointer',
              }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(0,145,255,0.08)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            >
              <div style={{ fontWeight: 600 }}>{shortName(r)}</div>
              <div style={{ fontSize: 11, color: 'var(--text-muted, #6b7280)', marginTop: 2 }}>
                {r.display_name}
              </div>
            </button>
          ))}
        </div>
      )}

      {open && !loading && results.length === 0 && q.length >= 3 && (
        <div style={{
          position: 'absolute',
          top: '100%', left: 0, right: 0,
          marginTop: 4,
          padding: '10px 14px',
          background: 'var(--bg-surface, white)',
          border: '1px solid var(--border, #d1d5db)',
          borderRadius: 6,
          fontSize: 12,
          color: 'var(--text-muted, #6b7280)',
          zIndex: 20,
        }}>
          Sin resultados para "{q}". Probá con calle + comuna o nombre del lugar.
        </div>
      )}
    </div>
  )
}


// ─── Helpers ──────────────────────────────────────────────────────────────

function shortName(result) {
  const a = result.address || {}
  // Prioridad: nombre del lugar > calle + número > display_name truncado
  return a.shop || a.amenity || a.building || a.commercial
      || (a.road ? `${a.road}${a.house_number ? ' ' + a.house_number : ''}` : null)
      || (result.display_name || '').split(',').slice(0, 2).join(',')
}
